import { useState, useCallback } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableCombobox } from '@/components/ui/searchable-combobox'
import { toast } from 'sonner'
import { commonSkincare } from '@/data/common-skincare'
import type { SkincareTemplate } from '@/data/common-skincare'
import type { SkincareRecommendation } from '@/types/documentation'

interface SkincarePanelProps {
  skincare: SkincareRecommendation[]
  onAdd: (rec: SkincareRecommendation) => void
  onUpdate: (index: number, rec: SkincareRecommendation) => void
  onRemove: (index: number) => void
  disabled: boolean
}

interface SkincareFormData {
  product_type: string
  recommendation: string
  when_to_use: string
}

const skincareFilterFn = (s: SkincareTemplate, q: string) =>
  s.recommendation.toLowerCase().includes(q) ||
  s.product_type.toLowerCase().includes(q) ||
  s.category.toLowerCase().includes(q)

function SkincareCombobox({
  onSelect,
  onCustom,
}: {
  onSelect: (template: SkincareTemplate) => void
  onCustom: () => void
}) {
  const renderItem = useCallback(
    (s: SkincareTemplate) => (
      <>
        <span className="text-xs font-medium text-slate-900">{s.recommendation}</span>
        <span className="text-xs text-slate-500">
          {s.product_type} · {s.when_to_use}
        </span>
      </>
    ),
    [],
  )

  return (
    <SearchableCombobox
      items={commonSkincare}
      filterFn={skincareFilterFn}
      onSelect={onSelect}
      renderItem={renderItem}
      trigger={
        <Button variant="outline" size="sm">
          <Plus className="h-4 w-4 mr-1" />
          Add Skincare Product
        </Button>
      }
      placeholder="Search skincare products..."
      searchLabel="Search skincare products"
      resultsLabel="Skincare search results"
      emptyText="No matching products"
      footer={(close) => (
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-xs"
          onClick={() => {
            onCustom()
            close()
          }}
        >
          <Plus className="h-3 w-3 mr-1" />
          Custom skincare product...
        </Button>
      )}
    />
  )
}

function SkincareForm({
  initial,
  onSave,
  onCancel,
  saveLabel,
}: {
  initial?: SkincareRecommendation
  onSave: (data: SkincareFormData) => void
  onCancel: () => void
  saveLabel?: string
}) {
  const [productType, setProductType] = useState(initial?.product_type ?? '')
  const [recommendation, setRecommendation] = useState(initial?.recommendation ?? '')
  const [whenToUse, setWhenToUse] = useState(initial?.when_to_use ?? '')

  const isValid = productType.trim().length > 0 && recommendation.trim().length > 0

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValid) return
    onSave({
      product_type: productType.trim(),
      recommendation: recommendation.trim(),
      when_to_use: whenToUse.trim(),
    })
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-md border p-4 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="sc-product-type" className="text-xs">
            Product Type
          </Label>
          <Input
            id="sc-product-type"
            value={productType}
            onChange={(e) => setProductType(e.target.value)}
            placeholder="e.g., Cleanser, Moisturizer"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="sc-when-to-use" className="text-xs">
            When to Use
          </Label>
          <Input
            id="sc-when-to-use"
            value={whenToUse}
            onChange={(e) => setWhenToUse(e.target.value)}
            placeholder="e.g., Morning and evening"
            className="mt-1"
          />
        </div>
        <div className="col-span-2">
          <Label htmlFor="sc-recommendation" className="text-xs">
            Product / Recommendation
          </Label>
          <Input
            id="sc-recommendation"
            value={recommendation}
            onChange={(e) => setRecommendation(e.target.value)}
            placeholder="e.g., CeraVe Foaming Facial Cleanser"
            className="mt-1"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" type="submit" disabled={!isValid}>
          {saveLabel ?? 'Save'}
        </Button>
        <Button size="sm" variant="ghost" type="button" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

export default function SkincarePanel({
  skincare,
  onAdd,
  onUpdate,
  onRemove,
  disabled,
}: SkincarePanelProps) {
  const [showCustomForm, setShowCustomForm] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  const isDuplicate = (recommendation: string, excludeIndex?: number) => {
    return skincare.some(
      (s, i) =>
        s.recommendation.toLowerCase() === recommendation.toLowerCase() &&
        i !== excludeIndex,
    )
  }

  const handleSelectTemplate = (template: SkincareTemplate) => {
    if (isDuplicate(template.recommendation)) {
      toast.warning(`${template.recommendation} is already in the list`)
      return
    }
    onAdd({
      product_type: template.product_type,
      recommendation: template.recommendation,
      when_to_use: template.when_to_use,
    })
  }

  const handleCustomSave = (data: SkincareFormData) => {
    if (isDuplicate(data.recommendation)) {
      toast.warning(`${data.recommendation} is already in the list`)
      return
    }
    onAdd(data)
    setShowCustomForm(false)
  }

  const handleEditSave = (index: number, data: SkincareFormData) => {
    if (isDuplicate(data.recommendation, index)) {
      toast.warning(`${data.recommendation} is already in the list`)
      return
    }
    onUpdate(index, data)
    setEditingIndex(null)
  }

  if (disabled) {
    return (
      <div className="space-y-2">
        {skincare.map((rec, i) => (
          <div key={i} className="rounded-md border p-3">
            <p className="text-sm font-medium text-slate-900">
              {rec.recommendation}
            </p>
            <p className="text-xs text-slate-500">
              {rec.product_type} · {rec.when_to_use}
            </p>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {skincare.length > 0 && (
        <div className="space-y-2">
          {skincare.map((rec, i) =>
            editingIndex === i ? (
              <SkincareForm
                key={i}
                initial={rec}
                saveLabel="Update"
                onSave={(data) => handleEditSave(i, data)}
                onCancel={() => setEditingIndex(null)}
              />
            ) : (
              <div
                key={i}
                className="flex items-center justify-between rounded-md border p-3"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-900">
                      {rec.recommendation}
                    </p>
                  </div>
                  <p className="text-xs text-slate-500">
                    {rec.product_type} · {rec.when_to_use}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingIndex(i)}
                    aria-label={`Edit ${rec.recommendation}`}
                  >
                    <Pencil className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemove(i)}
                    aria-label={`Remove ${rec.recommendation}`}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
                  </Button>
                </div>
              </div>
            ),
          )}
        </div>
      )}

      {showCustomForm ? (
        <SkincareForm
          onSave={handleCustomSave}
          onCancel={() => setShowCustomForm(false)}
        />
      ) : (
        <SkincareCombobox
          onSelect={handleSelectTemplate}
          onCustom={() => setShowCustomForm(true)}
        />
      )}
    </div>
  )
}
